
import { Facebook, Twitter, Linkedin, Link as LinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ShareLinkProps {
  url: string;
  title: string;
  platform: "facebook" | "twitter" | "linkedin" | "copy";
  className?: string;
}

const ShareLink = ({ url, title, platform, className }: ShareLinkProps) => {
  // Prepare share URLs for each platform
  const shareUrls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    copy: "#" // Special case for copy link
  };
  
  // Icon mapping
  const icons = {
    facebook: <Facebook className="h-4 w-4" />,
    twitter: <Twitter className="h-4 w-4" />,
    linkedin: <Linkedin className="h-4 w-4" />,
    copy: <LinkIcon className="h-4 w-4" />
  };
  
  // Labels
  const labels = {
    facebook: "Facebook",
    twitter: "Twitter",
    linkedin: "LinkedIn",
    copy: "Copia Link"
  };
  
  // Special handling for copy link
  if (platform === "copy") {
    return (
      <Button 
        variant="outline" 
        className={cn("flex items-center gap-2", className)}
        onClick={() => {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(url);
          }
        }}
      >
        {icons[platform]}
        {labels[platform]}
      </Button>
    );
  }
  
  // Return proper anchor tag for social platforms
  return (
    <a 
      href={shareUrls[platform]} 
      target="_blank" 
      rel="noopener noreferrer"
      className={cn("inline-block", className)}
    >
      <Button variant="outline" className="flex items-center gap-2">
        {icons[platform]}
        {labels[platform]}
      </Button>
    </a>
  );
};

interface BlogPostShareLinksProps {
  title: string;
  className?: string;
}

export const BlogPostShareLinks = ({ title, className }: BlogPostShareLinksProps) => {
  const url = typeof window !== "undefined" ? window.location.href : "";
  
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      <ShareLink url={url} title={title} platform="facebook" />
      <ShareLink url={url} title={title} platform="twitter" />
      <ShareLink url={url} title={title} platform="linkedin" />
      <ShareLink url={url} title={title} platform="copy" />
    </div>
  );
};
